import * as React from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { tokens, Icon, Text } from '@hotjar/hotsauce';

import type { Highlight, HighlightType } from '@hotjar/webapp/domains/highlights/model';

import { CloseButton } from './CloseButton';
import { DownloadButton } from '../Heatmaps/DownloadButton';
import { RecordingDownloadButton } from './RecordingDownloadButton';
import { ShortLink } from '../../ShortLink/ShortLink';
import { SkeletonLoader } from './SkeletonLoader';
import { HighlightActionDropdown } from '../HighlightActionDropdown';

const HighlightTypeSkeleton = styled(SkeletonLoader)`
  width: 85px;
  height: 32px;
`;

const HeaderButtonSkeleton = styled(SkeletonLoader)`
  width: 32px;
  height: 32px;
`;

const Wrapper = styled.header`
  align-items: center;
  background-color: ${tokens.color.ui_background};
  border-bottom: 1px solid ${tokens.color.ui_border_light};
  display: grid;
  grid-column-gap: ${tokens.spacer.x0};
  grid-template-columns: minmax(auto, 100%) auto auto auto auto;
  justify-content: flex-end;
  padding: ${tokens.spacer.x2} ${tokens.spacer.x1};
  padding-left: ${tokens.spacer.x2};
`;

const HeadingText = styled(Text)`
  align-items: center;
  display: grid;
  grid-column-gap: ${tokens.spacer.x0};
  grid-template-columns: auto auto;
  justify-content: flex-start;
  margin: 0;
`;

function Heading({ highlightType }: { highlightType: HighlightType }): JSX.Element {
  const { t } = useTranslation();

  // eslint-disable-next-line consistent-return
  function getIconAndText(): [React.ComponentProps<typeof Icon>['icon'], string] {
    // eslint-disable-next-line default-case
    switch (highlightType) {
      case 'heatmap':
        return ['cursor_select', t('highlights:heatmaps.heading')];
      case 'recording':
        return ['scissors', t('highlights:recordings.heading')];
      case 'unknown':
        return ['question_cards', t('highlights:unknown.heading')];
    }
  }

  const [icon, text] = getIconAndText();

  return (
    <HeadingText as="p" textType="subtitle2">
      <Icon icon={icon} />
      {text}
    </HeadingText>
  );
}

interface HeaderProps {
  authorIsCurrentUser: boolean;
  className?: string;
  highlight?: Highlight;
  onClose: () => void;
  siteId: string;
  svgRef: React.ComponentProps<typeof DownloadButton>['svgRef'];
}

export function Header({
  authorIsCurrentUser,
  className,
  highlight,
  onClose,
  siteId,
  svgRef,
}: HeaderProps): JSX.Element {
  if (!highlight) {
    return (
      <Wrapper className={className}>
        <HighlightTypeSkeleton />
        <HeaderButtonSkeleton />
        <HeaderButtonSkeleton />
        <HeaderButtonSkeleton />
        <CloseButton onClose={onClose} />
      </Wrapper>
    );
  }

  return (
    <Wrapper className={className}>
      <Heading highlightType={highlight.type} />
      <ShortLink
        highlightType={highlight.type}
        highlightId={highlight.id}
        uiPosition="highlight_modal"
      />
      {highlight.type === 'heatmap' ? (
        <DownloadButton svgRef={svgRef} highlight={highlight} uiPosition="highlight_modal" />
      ) : null}
      {highlight.type === 'recording' ? (
        <RecordingDownloadButton highlight={highlight} uiPosition="highlight_modal" />
      ) : null}
      <HighlightActionDropdown
        siteId={siteId}
        uiPosition="highlight_modal"
        highlight={highlight}
        onCloseDeleteModal={onClose}
        authorIsCurrentUser={authorIsCurrentUser}
      />
      <CloseButton onClose={onClose} />
    </Wrapper>
  );
}
